import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Stack, Link as MuiLink, Typography, Checkbox } from '@mui/material';

import { InfoField, InfoFieldChangeHandler } from '../../../../../components';

import { ProfileStatus } from '../../../../../enums';
import { UTCToDateTime } from '../../../../../utils/dates';
import type { ProfileWithInfo } from '../../../../../types';
import { RouterPaths } from '../../../../../router/paths';
import { Link, useParams } from 'react-router-dom';
import { UpdateProfileProviderDTO } from '../../../../../api';
import { useGetProvidersQuery } from '../../../../../api/controllers/providers';

type GeneralInfoProps = {
  profile: ProfileWithInfo | undefined;
  isEditMode: boolean;
  isEditProviderMode: boolean;
  fields: Partial<ProfileWithInfo> | null;
  providerFields: UpdateProfileProviderDTO['body'] | null;
  fieldsHandler: InfoFieldChangeHandler;
  fieldsHandlerProvider: InfoFieldChangeHandler;
  isLoading?: boolean;
};

export const GeneralInfo: FC<GeneralInfoProps> = (props) => {
  const {
    isEditMode,
    profile,
    fields,
    fieldsHandler,
    isLoading,
    providerFields,
    fieldsHandlerProvider,
    isEditProviderMode,
  } = props;
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { data: providers } = useGetProvidersQuery(
    {},
    { skip: !isEditProviderMode || !profileUid }
  );

  const statusSelectOptions = Object.values(ProfileStatus).map((status) => {
    return (
      <MenuItem key={status} value={status}>
        {t(`common.profile-status.${status}`)}
      </MenuItem>
    );
  });
  const providersSelectOptions = providers?.providers.map((provider) => {
    return (
      <MenuItem key={provider} value={provider}>
        {provider}
      </MenuItem>
    );
  });

  return (
    <Stack direction="column" spacing={1}>
      <InfoField name={t('profile-details.general.uid')} value={profile?.uid} />

      <InfoField
        name={t('profile-details.general.kyc-sync-profile-uid')}
        value={profile?.kyc_sync_profile_uid}
      />

      <InfoField
        name={t('profile-details.general.reference-number')}
        value={profile?.reference_number}
      />

      <InfoField
        name={t('profile-details.general.created-at')}
        value={profile?.created_at}
        formattedValue={profile?.created_at && UTCToDateTime(profile.created_at)}
      />

      <InfoField
        name={t('profile-details.general.type')}
        value={profile?.type}
        formattedValue={profile?.type && t(`common.profile-type.${profile.type}`)}
      />

      <InfoField
        name={t('profile-details.general.status')}
        value={profile?.status}
        formattedValue={profile?.status && t(`common.profile-status.${profile.status}`)}
        editProps={{ withEditMode: !profile?.read_only, isEditMode }}
        inputProps={{
          name: 'status',
          value: fields?.status ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: statusSelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.general.status-reason')}
        value={profile?.status_reason}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'status_reason',
          value: fields?.status_reason ?? '',
          onChange: fieldsHandler,
          componentType: 'textarea',
        }}
        isLoading={isLoading}
        withEmptyValue={
          fields?.status === ProfileStatus.BLOCKED ||
          fields?.status === ProfileStatus.ARCHIVED ||
          fields?.status === ProfileStatus.REJECTED
        }
        // hint={t('profile-details.general.status-reason-hint')}
      />
      <InfoField
        name={t('profile-details.general.sumsub-applicant-id')}
        component={
          profile?.sumsub_applicant_id ? (
            <MuiLink
              underline="hover"
              component={Link}
              target="_blank"
              to={`${process.env.REACT_APP_SUMSUB_URL}/checkus/applicant/${profile?.sumsub_applicant_id}/basicInfo`}
            >
              <Typography>{profile?.sumsub_applicant_id}</Typography>
            </MuiLink>
          ) : undefined
        }
        value={profile?.sumsub_applicant_id}
      />

      <InfoField
        name={t('profile-details.general.provider')}
        value={profile?.default_provider}
        editProps={{ withEditMode: !profile?.read_only, isEditMode: isEditProviderMode }}
        inputProps={{
          name: 'provider',
          value: providerFields?.provider ?? '',
          onChange: fieldsHandlerProvider,
          componentType: 'select',
          selectOptions: providersSelectOptions,
        }}
        withEmptyValue
        isLoading={isLoading}
      />

      {isEditProviderMode && (
        <InfoField
          name={t('profile-details.general.with-wallets-provider-change')}
          value={providerFields?.cascade}
          editProps={{ withEditMode: !profile?.read_only, isEditMode: isEditProviderMode }}
          inputProps={{
            name: 'cascade',
            value: providerFields?.cascade ?? '',
            onChange: fieldsHandlerProvider,
            componentType: 'checkbox',
          }}
          withEmptyValue
          isLoading={isLoading}
        />
      )}
    </Stack>
  );
};
